import React from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'react-multi-select-component';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';

function CardDrag({
  item,
  index,
  handleCheckboxST,
  handleChanged,
  renderCopy,
  renderTooltipActiveInactive,
  activeInactiveClicked,
  renderTooltipDelete,
  handleDelete,
  copySampleTracking,
  isUniqueId,
  masterAddEditAccess,
  masterDeleteAccess,
  brandList = [],
  onBrandSelectionChange,
  brandValidationErrors = {},
  cardLength,
}) {
  const { t } = useTranslation('common');

  // Prepare brand options for multiselect dropdown - only include active brands
  const savedBrandIds = new Set((item?.savedBrands || []).map((brandId) => String(brandId)));
  const brandOptions = brandList
    .filter((brand) => brand.isActive)
    .map((brand) => ({
      value: brand._id,
      label: brand.name,
      disabled: Boolean(item?.inUse) && savedBrandIds.has(String(brand._id)),
    }));

  const getSelectedBrands = () => {
    if (!item?.brands || !Array.isArray(item?.brands)) return [];

    return item?.brands
      ?.map((brandId) => {
        const brandIdString = brandId;
        const brand = brandList.find((b) => b._id === brandIdString);
        return brand ? { value: brand._id, label: brand.name } : null;
      })
      .filter(Boolean);
  };

  // Handle brand selection change
  const handleBrandChange = (selectedBrands) => {
    if (onBrandSelectionChange) {
      onBrandSelectionChange(index, selectedBrands);
    }
  };

  const sampleTrackingNameInput = (
    <input
      key={index}
      name="sampleTracking"
      autoFocus={!item?._id}
      value={item?.name}
      placeholder={t('CARD_DRAGS.ENTER_SAMPLE_TRACKING')}
      type="text"
      onChange={(event) => handleChanged(event, index)}
      disabled={!masterAddEditAccess || item?.inUse}
    />
  );

  return index === 0 ? (
    <li key={index}>
      <div className="input-container w-100">
        {/* Brand Multiselect Dropdown - only show for non-disabled items */}
        {masterAddEditAccess && (
          <div className="mb-1">
            <div className="common-react-select three-option-visible mb-1">
              <MultiSelect
                name="brands"
                options={brandOptions}
                value={getSelectedBrands()}
                className="product-custom-multiselect common-new-multiselect w-100"
                labelledBy={t('SAMPLE_TRACK.SELECT_BRANDS_PLACEHOLDER')}
                overrideStrings={{
                  selectSomeItems: t('SAMPLE_TRACK.SELECT_BRANDS_PLACEHOLDER'),
                  search: t('SAMPLE_TRACK.SEARCH_BRANDS'),
                }}
                ClearSelectedIcon={null}
                onChange={(selected) => {
                  handleBrandChange(selected);
                }}
              />
              {brandValidationErrors[index] && <label className="text-danger m-0 p-1">{brandValidationErrors[index]}</label>}
            </div>
          </div>
        )}
        <div className="d-flex align-items-center">
          <div className="common-checkbox size-sm">
            <input
              type="checkbox"
              name="sampleTracking"
              id={`sampleTracking_${index}`}
              value={item?._id}
              onChange={handleCheckboxST}
              checked={item?.isChecked === true}
              disabled={!masterAddEditAccess || item?.inUse}
            />
            <label htmlFor={`sampleTracking_${index}`} className="chklabel">
              <span />
            </label>
          </div>

          <input
            key={index}
            name="sampleTracking"
            autoFocus={!item?._id}
            value={item?.name}
            disabled
            placeholder={t('CARD_DRAGS.ENTER_SAMPLE_TRACKING')}
            type="text"
            onChange={(e) => handleChanged(e, index)}
          />
        </div>
      </div>
    </li>
  ) : (
    <div>
      <li key={index}>
        <div className="input-container w-100">
          {/* Brand Multiselect Dropdown - only show for non-disabled items */}
          {masterAddEditAccess && (
            <div className="mb-2">
              <div className={`common-react-select ${cardLength > 3 && index >= cardLength - 2 ? 'placement-top' : ''} three-option-visible mb-1`}>
                <MultiSelect
                  name="brands"
                  options={brandOptions}
                  value={getSelectedBrands()}
                  className="product-custom-multiselect common-new-multiselect w-100"
                  labelledBy={t('SAMPLE_TRACK.SELECT_BRANDS_PLACEHOLDER')}
                  overrideStrings={{
                    selectSomeItems: t('SAMPLE_TRACK.SELECT_BRANDS_PLACEHOLDER'),
                    search: t('SAMPLE_TRACK.SEARCH_BRANDS'),
                  }}
                  ClearSelectedIcon={null}
                  onChange={(selected) => {
                    handleBrandChange(selected);
                  }}
                />
                {brandValidationErrors[index] && <label className="text-danger m-0 p-1">{brandValidationErrors[index]}</label>}
              </div>
            </div>
          )}

          <div className="d-flex align-items-center">
            <div className="common-checkbox size-sm">
              <input
                type="checkbox"
                name="sampleTracking"
                id={`sampleTracking_${index}`}
                value={item?._id}
                onChange={handleCheckboxST}
                checked={item?.isChecked === true}
                disabled={!masterAddEditAccess || item?.inUse}
              />
              <label htmlFor={`sampleTracking_${index}`} className="chklabel">
                <span />
              </label>
            </div>
            {item?.inUse ? (
              <CustomTooltip tooltipText={t('SAMPLE_TRACK.IN_USE_TOOLTIP')} placement="top">
                <span className="d-inline-block w-100">{sampleTrackingNameInput}</span>
              </CustomTooltip>
            ) : (
              sampleTrackingNameInput
            )}
            {item?.name !== 'Requested' && (
              <div className="input-action">
                {item?._id !== '' && (
                  <>
                    {item?.isTrakingURL && masterAddEditAccess && (
                      <OverlayTrigger placement="auto" delay={{ show: 250, hide: 400 }} overlay={renderCopy}>
                        <button id={item?._id} onClick={() => copySampleTracking()} type="button" aria-label={t('CARD_DRAGS.COPY')}>
                          <span className="icon-copy" />
                        </button>
                      </OverlayTrigger>
                    )}
                    {masterAddEditAccess && (
                      <OverlayTrigger placement="auto" delay={{ show: 250, hide: 400 }} overlay={renderTooltipActiveInactive}>
                        <button
                          onClick={() => activeInactiveClicked(item?._id, 'sampleTracking')}
                          id={item?._id}
                          type="button"
                          aria-label={item?.isActive ? t('CARD_DRAGS.ACTIVE') : t('CARD_DRAGS.INACTIVE')}
                        >
                          <span className={item?.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                        </button>
                      </OverlayTrigger>
                    )}
                  </>
                )}
                {!(item?.name === 'Sent' || item?.name === 'Received') && masterDeleteAccess && (
                  <OverlayTrigger placement="auto" delay={{ show: 250, hide: 400 }} overlay={renderTooltipDelete}>
                    <button
                      onClick={() => {
                        handleDelete(item?._id ? item?._id : index, 'sampleTracking');
                      }}
                      type="button"
                      aria-label={t('CARD_DRAGS.DELETE')}
                    >
                      <span className="icon-delete mt-3 f-20" />
                    </button>
                  </OverlayTrigger>
                )}
              </div>
            )}
          </div>
          {isUniqueId && isUniqueId.length !== 0 && isUniqueId.includes(index) && (
            <label className="text-danger m-0 p-1">{t('CARD_DRAGS.ENTRY_ALREADY_EXIST')}</label>
          )}

          {item.isEmpty && (item.name === null || item.name === '') && (
            <label className="text-danger m-0 p-1">{t('CARD_DRAGS.SAMPLE_TRACKING_REQUIRED')}</label>
          )}

          {item.isNew && item.name && item.name.length && !item.name.trim().length && (
            <label className="text-danger m-0 p-1">{t('CARD_DRAGS.INVALID_SAMPLE_TRACKING_ENTRY')}</label>
          )}
        </div>
      </li>
    </div>
  );
}
export default CardDrag;
